import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTheme } from '@/app/ThemeProvider';
import { decimateMinMax } from '../decoder';

import styles from './WaveformCanvas.module.css';

export interface WaveformCanvasProps {
  samples: Int16Array;
  /** Inclusive start index of the visible window. */
  start: number;
  /** Exclusive end index of the visible window. */
  end: number;
  onWindowChange: (start: number, end: number) => void;
  height?: number;
}

interface Cursor {
  x: number;
  index: number;
  value: number;
}

/**
 * A raw EEG trace on a canvas.
 *
 * SVG or a charting library would mount one node per point; a session is
 * routinely 400,000 samples. Instead each pixel column is drawn from the true
 * min and max of the samples that land in it, so the envelope of the signal —
 * the part that actually matters in an EEG trace — survives at every zoom
 * level, and the cost is bounded by the canvas width rather than the file
 * size. Wheel zooms around the pointer, drag pans.
 */
export function WaveformCanvas({
  samples,
  start,
  end,
  onWindowChange,
  height = 260,
}: WaveformCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(800);
  const [cursor, setCursor] = useState<Cursor | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragFrom = useRef<{ x: number; start: number; end: number } | null>(null);
  const { resolved } = useTheme();

  // Track the element's real width so the canvas is never stretched.
  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.max(200, Math.round(entry.contentRect.width)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.height = `${height}px`;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);

    const css = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string) =>
      css.getPropertyValue(name).trim() || fallback;

    const surface = read('--bg-inset', '#0d1119');
    const grid = read('--viz-grid', 'rgba(255,255,255,0.07)');
    const trace = read('--signal', '#22d3ee');
    const axisColor = read('--viz-axis', '#6b7a94');

    context.clearRect(0, 0, width, height);
    context.fillStyle = surface;
    context.fillRect(0, 0, width, height);

    const visibleStart = Math.max(0, Math.min(start, samples.length));
    const visibleEnd = Math.max(visibleStart + 1, Math.min(end, samples.length));
    const { min, max, columns } = decimateMinMax(samples, visibleStart, visibleEnd, width);

    // Symmetric amplitude scale around zero: an EEG trace is bipolar, and an
    // asymmetric axis would misrepresent it.
    let peak = 1;
    for (let i = 0; i < columns; i += 1) {
      peak = Math.max(peak, Math.abs(min[i] ?? 0), Math.abs(max[i] ?? 0));
    }
    peak *= 1.08;

    const midY = height / 2;
    const scale = midY / peak;
    const toY = (value: number) => midY - value * scale;

    // Grid: the zero line plus quarter divisions, kept recessive.
    context.strokeStyle = grid;
    context.lineWidth = 1;
    context.beginPath();
    for (const fraction of [0.25, 0.5, 0.75]) {
      const y = Math.round(height * fraction) + 0.5;
      context.moveTo(0, y);
      context.lineTo(width, y);
    }
    context.stroke();

    context.strokeStyle = axisColor;
    context.globalAlpha = 0.45;
    context.beginPath();
    context.moveTo(0, Math.round(midY) + 0.5);
    context.lineTo(width, Math.round(midY) + 0.5);
    context.stroke();
    context.globalAlpha = 1;

    // Two rendering modes, the way a signal editor does it.
    //
    // Zoomed in far enough that a column holds at most a couple of samples,
    // the real waveform is drawn as a connected line. Zoomed out, a column
    // covers hundreds of samples and no line is meaningful, so the true
    // min/max envelope is drawn instead — and drawn semi-transparent, because
    // a solid slab of colour reads as a filled area rather than dense signal
    // and hides the density variation that is the point of the view.
    const samplesPerColumn = (visibleEnd - visibleStart) / Math.max(1, columns);
    const xOf = (column: number) =>
      columns === 1 ? 0.5 : (column / (columns - 1)) * (width - 1) + 0.5;

    context.strokeStyle = trace;

    if (samplesPerColumn <= 2) {
      context.lineWidth = 1.4;
      context.lineJoin = 'round';
      context.beginPath();
      for (let column = 0; column < columns; column += 1) {
        const y = toY(((max[column] ?? 0) + (min[column] ?? 0)) / 2);
        if (column === 0) context.moveTo(xOf(column), y);
        else context.lineTo(xOf(column), y);
      }
      context.stroke();
    } else {
      // Denser columns get more transparent, so the overlap itself encodes
      // how much signal each pixel is standing in for.
      context.globalAlpha = samplesPerColumn > 64 ? 0.52 : 0.72;
      context.lineWidth = 1;
      context.beginPath();
      for (let column = 0; column < columns; column += 1) {
        const x = xOf(column);
        const top = toY(max[column] ?? 0);
        const bottom = toY(min[column] ?? 0);
        context.moveTo(x, top);
        context.lineTo(x, Math.max(bottom, top + 0.6));
      }
      context.stroke();
      context.globalAlpha = 1;
    }

    // Crosshair.
    if (cursor) {
      context.strokeStyle = axisColor;
      context.lineWidth = 1;
      context.setLineDash([3, 3]);
      context.beginPath();
      context.moveTo(Math.round(cursor.x) + 0.5, 0);
      context.lineTo(Math.round(cursor.x) + 0.5, height);
      context.stroke();
      context.setLineDash([]);

      // A ring in the surface colour keeps the dot legible over the trace.
      const y = toY(cursor.value);
      context.fillStyle = trace;
      context.strokeStyle = surface;
      context.lineWidth = 2;
      context.beginPath();
      context.arc(cursor.x, y, 3.5, 0, Math.PI * 2);
      context.fill();
      context.stroke();
    }
  }, [samples, start, end, width, height, cursor, resolved]);

  useEffect(() => {
    const id = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(id);
  }, [draw]);

  const indexAt = useCallback(
    (offsetX: number) => {
      const visibleStart = Math.max(0, Math.min(start, samples.length));
      const visibleEnd = Math.max(visibleStart + 1, Math.min(end, samples.length));
      const ratio = Math.max(0, Math.min(1, offsetX / Math.max(1, width)));
      return Math.min(
        samples.length - 1,
        visibleStart + Math.floor(ratio * (visibleEnd - visibleStart)),
      );
    },
    [start, end, samples.length, width],
  );

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;

    if (dragging && dragFrom.current) {
      const from = dragFrom.current;
      const span = from.end - from.start;
      const shift = Math.round(((from.x - x) / Math.max(1, width)) * span);
      let nextStart = from.start + shift;
      let nextEnd = from.end + shift;
      if (nextStart < 0) {
        nextEnd -= nextStart;
        nextStart = 0;
      }
      if (nextEnd > samples.length) {
        nextStart -= nextEnd - samples.length;
        nextEnd = samples.length;
      }
      onWindowChange(Math.max(0, nextStart), Math.min(samples.length, nextEnd));
      return;
    }

    const index = indexAt(x);
    setCursor({ x, index, value: samples[index] ?? 0 });
  };

  const onWheel = (event: React.WheelEvent<HTMLCanvasElement>) => {
    if (samples.length === 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / Math.max(1, width)));

    const span = end - start;
    const factor = event.deltaY > 0 ? 1.25 : 0.8;
    const nextSpan = Math.max(64, Math.min(samples.length, Math.round(span * factor)));
    const anchor = start + ratio * span;

    let nextStart = Math.round(anchor - ratio * nextSpan);
    let nextEnd = nextStart + nextSpan;
    if (nextStart < 0) {
      nextStart = 0;
      nextEnd = nextSpan;
    }
    if (nextEnd > samples.length) {
      nextEnd = samples.length;
      nextStart = Math.max(0, nextEnd - nextSpan);
    }
    onWindowChange(nextStart, nextEnd);
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <canvas
        ref={canvasRef}
        className={clsx(styles.canvas, dragging && styles.dragging)}
        style={{ height }}
        role="img"
        aria-label={`EEG waveform, samples ${start} to ${end} of ${samples.length}`}
        onPointerMove={onPointerMove}
        onPointerLeave={() => {
          setCursor(null);
          setDragging(false);
          dragFrom.current = null;
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          const rect = event.currentTarget.getBoundingClientRect();
          dragFrom.current = { x: event.clientX - rect.left, start, end };
          setDragging(true);
        }}
        onPointerUp={(event) => {
          event.currentTarget.releasePointerCapture(event.pointerId);
          setDragging(false);
          dragFrom.current = null;
        }}
        onWheel={onWheel}
      />

      {cursor ? (
        <div className={styles.readout}>
          <span>
            sample <span className={styles.readoutValue}>{cursor.index.toLocaleString('en-US')}</span>
          </span>
          <span>
            value <span className={styles.readoutValue}>{cursor.value}</span>
          </span>
        </div>
      ) : (
        <div className={styles.hint}>Scroll to zoom · drag to pan</div>
      )}
    </div>
  );
}
